import { observer } from 'mobx-react-lite';
import { SearchRootDirectoryText, SearchText } from './FilePickerPane.locstring.json';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import changeListViewItemsSorting from '../actions/changeListViewItemsSorting';
import changeSelectedFolder from '../actions/changeSelectedFolder';
import closeFilePicker from '../actions/closeFilePicker';
import exitSearch from '../actions/search/exitSearch';
import FilePickerErrorMessageView from './FilePickerErrorMessageView';
import FilePickerGroupsView from './GroupsView/FilePickerGroupsView';
import FilePickerListView from './FilePickerListView';
import FilePickerPaneBottomPart from './FilePickerPaneBottomPart';
import FilePickerPanelViewState from '../store/schema/FilePickerPanelViewState';
import FilePickerPaneTopPart, { SearchBoxProps } from './FilePickerPaneTopPart';
import FilePickerTileView from './TileView/FilePickerTileView';
import loadMoreItemsForSelectedProvider from '../actions/loadMoreItemsForSelectedProvider';
import OneDriveConsumerScope from 'owa-service/lib/contract/OneDriveConsumerScope';
import OneDriveConsumerScopeType from 'owa-service/lib/contract/OneDriveConsumerScopeType';
import OneDriveProScope from 'owa-service/lib/contract/OneDriveProScope';
import OneDriveProScopeType from 'owa-service/lib/contract/OneDriveProScopeType';
import popFilePickerBreadcrumbsTill from '../actions/popFilePickerBreadcrumbsTill';
import setBreadCrumbsForSearch from '../actions/search/setBreadCrumbsForSearch';
import startSearch from '../actions/search/startSearch';
import { ComposeUniqueState } from '../store/schema/FilePickerUploadConfiguration';
import updateFileProviderItemsSelection from '../actions/updateFileProviderItemsSelection';
import { FilePickerAction } from 'owa-filepicker';
import { FilePickerBreadcrumb } from '../store/schema/FilePickerViewState';
import { FilePickerViewType } from '../types/FilePickerViewSwitcher';
import { getFileProviderInfo } from '../utils/fileProviderInfo';
import { getShouldHideInsertButton } from '../utils/getShouldHideInsertButton';
import { getDisabledAttachTooltip } from '../utils/getDisabledAttachTooltip';
import FileProviderItemViewState, {
    FileProviderItemType,
    FolderItemViewState,
} from '../store/schema/FileProviderItemViewState';
import { assertNever } from 'owa-assert';
import { logUsage } from 'owa-analytics';

const classNames = require('classnames');
const styles = require('./FilePickerPane.scss');

export interface FilePickerPaneProps extends React.HTMLProps<HTMLDivElement> {
    viewState: FilePickerPanelViewState;
    onFilesSelectedForNext: (
        files: FileProviderItemViewState[],
        composeUniqueState: ComposeUniqueState
    ) => void;
    onFilesSelectedForInsert: (
        files: FileProviderItemViewState[],
        composeUniqueState: ComposeUniqueState
    ) => void;
    onFilesSelectedForAttach: (
        files: FileProviderItemViewState[],
        composeUniqueState: ComposeUniqueState
    ) => void;
    maxAllowedLocalFilesSize?: number;
    selectedView: FilePickerViewType;
    isTopLevelGroupsView: boolean;
}

export default observer(function FilePickerPane(props: FilePickerPaneProps) {
    const getCurrentSupportedActions = (
        supportedActions: FilePickerAction[]
    ): FilePickerAction[] => {
        if (props.isTopLevelGroupsView) {
            return [];
        } else {
            return supportedActions;
        }
    };
    const getFilePickerItemView = (): JSX.Element => {
        if (!props.viewState.selectedFileProvider) {
            // If there is no selected file provider then we render an empty view
            return <span className={styles.emptyFilePickerView} />;
        }
        const {
            selectedFileProvider,
            filePickerErrorMessageViewState,
            filePickerViewState: {
                selectedFolder,
                selectedFileProviderItems,
                fileProviderItems,
                isLoadingItems,
                columnSorting: attachmentItemsSort,
            },
        } = props.viewState;
        // The scope SharedWithMe is treated differently for what columns we show and how we sort columns compared to other ODB scopes.
        // However, in SharedWithMe if the user opens a folder we use our normal ODB columns/sorting since these getItems calls don't go through
        // the SharedWithMe API. So we only want to treat the SharedWithMeScope as special if selectedFolder is null.
        const useScopeForColumnInfo: boolean = !selectedFolder;
        const fileProviderScope = props.viewState.selectedProviderScope;
        const fileProviderInfo = getFileProviderInfo(selectedFileProvider.type);
        let getFilePickerListColumnInfo = fileProviderInfo.getFilePickerListColumnInfo;
        // TODO VSO 61918 - move this logic to utils and add tests
        if (selectedFileProvider.type === AttachmentDataProviderType.OneDrivePro) {
            const oneDriveProScope: OneDriveProScope = fileProviderScope as OneDriveProScope;
            if (
                useScopeForColumnInfo &&
                oneDriveProScope.Type === OneDriveProScopeType.SharedWithMe
            ) {
                getFilePickerListColumnInfo = (name: string) =>
                    fileProviderInfo.getFilePickerListColumnInfo(
                        name,
                        false /* isRecentFiles */,
                        true /* isSharedWithMeRootFolder */
                    );
            } else if (oneDriveProScope.Type === OneDriveProScopeType.Recent) {
                getFilePickerListColumnInfo = (name: string) =>
                    fileProviderInfo.getFilePickerListColumnInfo(name, true /* isRecentFiles */);
            }
        } else if (selectedFileProvider.type === AttachmentDataProviderType.OneDriveConsumer) {
            const oneDriveConsumerScope = fileProviderScope as OneDriveConsumerScope;
            if (oneDriveConsumerScope.Type === OneDriveConsumerScopeType.RecentItems) {
                getFilePickerListColumnInfo = (name: string) =>
                    fileProviderInfo.getFilePickerListColumnInfo(name, true /* isRecentFiles */);
            }
        }
        const dataSourceId = selectedFolder?.key || 'root';
        if (filePickerErrorMessageViewState.isShown) {
            return <FilePickerErrorMessageView message={filePickerErrorMessageViewState.message} />;
        }
        switch (props.selectedView) {
            case FilePickerViewType.List:
                return (
                    <FilePickerListView
                        dataSourceId={dataSourceId}
                        getItemToRender={fileProviderInfo.getColumnRenderFunction(
                            useScopeForColumnInfo ? fileProviderScope : null
                        )}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={onLoadMoreRows}
                        isLoadRowsInProgress={isLoadingItems}
                        getCanLoadMore={getCanLoadMore}
                        getFilePickerListColumnInfo={getFilePickerListColumnInfo}
                        currentSorting={attachmentItemsSort}
                        isAnyItemSelected={selectedFileProviderItems.length > 0}
                        onItemSelected={onItemSelected}
                        onItemsSelectionUpdated={onItemsSelectionUpdated}
                        sortByColumn={sortByColumn}
                    />
                );
            case FilePickerViewType.Tile:
                return (
                    <FilePickerTileView
                        dataSourceId={dataSourceId}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={onLoadMoreRows}
                        isLoadRowsInProgress={isLoadingItems}
                        getCanLoadMore={getCanLoadMore}
                        onItemSelected={onItemSelected}
                        onItemsSelectionUpdated={onItemsSelectionUpdated}
                    />
                );
            case FilePickerViewType.Groups:
                return (
                    <FilePickerGroupsView
                        dataSourceId={dataSourceId}
                        items={fileProviderItems}
                        selectedItems={selectedFileProviderItems}
                        onLoadMoreRows={onLoadMoreRows}
                        isLoadRowsInProgress={isLoadingItems}
                        getCanLoadMore={getCanLoadMore}
                        onItemSelected={onItemSelected}
                        onItemsSelectionUpdated={onItemsSelectionUpdated}
                    />
                );
            default:
                assertNever(props.selectedView);
        }
        throw new Error(`Unrecognized view type ${props.selectedView}`);
    };
    const onLoadMoreRows = () => {
        loadMoreItemsForSelectedProvider(props.viewState);
    };
    const getCanLoadMore = () => {
        const filePickerViewState = props.viewState.filePickerViewState;
        return (
            filePickerViewState &&
            filePickerViewState.fileProviderItems.length < filePickerViewState.totalItemsCount
        );
    };
    const onNextClicked = () => {
        const { filePickerViewState } = props.viewState;
        if (filePickerViewState.selectedFileProviderItems.length === 0) {
            // NOOP if no files are selected. It shouldn't even happen because next should be disabled in this case.
            return;
        }
        props.onFilesSelectedForNext(
            filePickerViewState.selectedFileProviderItems,
            props.viewState.filePickerActionsViewState.uploadConfiguration.composeUniqueState
        );
    };
    const onInsertClicked = () => {
        const { filePickerViewState } = props.viewState;
        if (filePickerViewState.selectedFileProviderItems.length === 0) {
            // NOOP if no files are selected. It shouldn't even happen because next should be disabled in this case.
            return;
        }
        props.onFilesSelectedForInsert(
            filePickerViewState.selectedFileProviderItems,
            props.viewState.filePickerActionsViewState.uploadConfiguration.composeUniqueState
        );
    };
    const onAttachClicked = () => {
        const { filePickerViewState } = props.viewState;
        if (filePickerViewState.selectedFileProviderItems.length === 0) {
            // NOOP if no files are selected. It shouldn't even happen because next should be disabled in this case.
            return;
        }
        props.onFilesSelectedForAttach(
            filePickerViewState.selectedFileProviderItems,
            props.viewState.filePickerActionsViewState.uploadConfiguration.composeUniqueState
        );
    };
    const onItemsSelectionUpdated = (selectedItems: FileProviderItemViewState[]) => {
        const {
            maxAllowedLocalFilesSize,
            viewState: { selectedFileProvider },
        } = props;
        updateFileProviderItemsSelection(
            selectedItems,
            maxAllowedLocalFilesSize,
            selectedFileProvider,
            props.viewState
        );
    };
    const onItemSelected = (item: FileProviderItemViewState) => {
        if (item.type === FileProviderItemType.Folder || item.type === FileProviderItemType.Group) {
            const folder = item as FolderItemViewState;
            // If we open a folder from search we do not know if it is a child of the current folder or not.
            // We clear all folder bread crumbs, and add "..." to the scope breadscrumb in this case to avoid potentially showing an
            // incorrect breadcrumb chain.
            if (props.viewState.filePickerViewState.searchViewState.isInSearchMode) {
                setBreadCrumbsForSearch(
                    props.viewState.selectedFileProvider,
                    props.viewState.selectedProviderScope,
                    props.viewState.filePickerViewState
                );
            }
            loadFolder(folder);
        } else if (item.type === FileProviderItemType.File) {
            // If a file is selected then we go with the same flow as clicking Next
            onNextClicked();
        }
    };
    const loadFolder = (folder: FolderItemViewState) => {
        changeSelectedFolder(folder, props.viewState);
    };
    const sortByColumn = (
        sortColumn: AttachmentItemsSortColumn,
        customSort?: (
            items: FileProviderItemViewState[],
            sortAsc: boolean
        ) => FileProviderItemViewState[]
    ) => {
        changeListViewItemsSorting(sortColumn, props.viewState, customSort);
    };
    const onBreadcrumbSelected = (breadcrumb: FilePickerBreadcrumb) => {
        popFilePickerBreadcrumbsTill(breadcrumb, props.viewState.filePickerViewState);
        loadFolder(breadcrumb.associatedItem);
    };
    const isSearchEnabled = () => {
        const { selectedFileProvider, selectedProviderScope } = props.viewState;
        if (!selectedFileProvider || !selectedFileProvider.model.IsSearchSupported) {
            return false;
        }
        if (selectedFileProvider.type === AttachmentDataProviderType.OneDrivePro) {
            const oDPScope: OneDriveProScope = selectedProviderScope;
            return oDPScope.Type === OneDriveProScopeType.MyDocuments;
        }
        return (
            selectedFileProvider.model.Scopes[selectedFileProvider.model.DefaultScopeIndex] ===
            selectedProviderScope
        );
    };
    const onSearch = (searchQuery: string) => {
        startSearch(props.viewState, searchQuery);
    };
    const onExitSearch = () => {
        exitSearch(props.viewState);
    };
    const onCancelClicked = () => {
        closeFilePicker(props.maxAllowedLocalFilesSize, props.viewState);
    };
    const {
        viewState: { filePickerViewState },
    } = props;
    const { breadcrumbs, isLoadingItems, supportedActions } = filePickerViewState;
    const currentSupportedActions = getCurrentSupportedActions(supportedActions);
    const searchLabelName = filePickerViewState.selectedFolder
        ? filePickerViewState.selectedFolder.name
        : filePickerViewState.breadcrumbs
        ? filePickerViewState.breadcrumbs[0]?.name
        : loc(SearchRootDirectoryText);
    if (!filePickerViewState.selectedFolder && !filePickerViewState.breadcrumbs[0]) {
        // VSO 79553 - remove this after investigating. Theory is that the filePicker is being rendered before being initialized
        // Marking as isCore to log at 100% as this is just to investigate an exception and will be removed soon
        logUsage(
            'FPNullBreadcrumbsOnRender',
            {
                loadState: props.viewState.preloadState,
                hasSelectedProvider: !!props.viewState.selectedFileProvider,
            },
            { isCore: true }
        );
    }
    const searchLabelText = format(loc(SearchText), searchLabelName);
    const isUsing3S =
        props.viewState.selectedFileProvider?.model.Type === AttachmentDataProviderType.MailMessage;
    const searchBoxProps: SearchBoxProps = {
        isSearchEnabled: isSearchEnabled(),
        onSearch: onSearch,
        onExitSearch: onExitSearch,
        labelText: searchLabelText,
        currentSearchText: filePickerViewState.searchViewState.searchQuery,
        isUsing3S: isUsing3S,
    };
    return (
        <div className={classNames([props.className, styles.container])}>
            <FilePickerPaneTopPart
                breadcrumbs={breadcrumbs}
                onBreadcrumbSelected={onBreadcrumbSelected}
                spinnerEnabled={isLoadingItems}
                searchBoxProps={searchBoxProps}
            />

            {getFilePickerItemView()}

            <FilePickerPaneBottomPart
                supportedActions={currentSupportedActions}
                shouldHideInsertButton={getShouldHideInsertButton(props.viewState)}
                disabledAttachTooltip={getDisabledAttachTooltip(
                    props.viewState,
                    props.maxAllowedLocalFilesSize
                )}
                onNextClicked={onNextClicked}
                onCancelClicked={onCancelClicked}
                onInsertClicked={onInsertClicked}
                onAttachClicked={onAttachClicked}
            />
        </div>
    );
});
